import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { Tournament } from '@airmail/engine';
import { Button, Dialog, Input, Select } from '@/ui';
import { createSeason, dayFromTournament, listSeasons, saveSeason, upsertDay, type Season, type SeasonDay } from '@/store/season';
import { useT } from '@/i18n';

/**
 * „In Saison übernehmen" (docs/13 §4): Tabellen des Spieltags in eine Saison
 * kopieren — aus dem offenen Turnier oder aus einer Spieltag-Datei (docs/17),
 * die eine andere Mannschaft ohne Internet geschickt hat.
 */
export function SeasonImportDialog({ tournament, day: dayProp, onClose, onDone }: { tournament?: Tournament; day?: SeasonDay; onClose: () => void; onDone?: (s: Season) => void }) {
  const t = useT();
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selected, setSelected] = useState<string>('new');
  const [newName, setNewName] = useState(t('Saison {y}', { y: new Date().getFullYear() }));
  const [done, setDone] = useState<Season | null>(null);
  useEffect(() => {
    listSeasons().then((s) => {
      setSeasons(s);
      if (s[0]) setSelected(s[0].id);
    });
  }, []);
  const day = dayProp ?? (tournament ? dayFromTournament(tournament) : null);
  const already = !!day && seasons.find((s) => s.id === selected)?.days.some((d) => d.tournamentId === day.tournamentId);

  const run = async () => {
    if (!day) return;
    let season = selected === 'new' ? await createSeason(newName.trim() || t('Saison')) : seasons.find((s) => s.id === selected);
    if (!season) return;
    season = upsertDay(season, day);
    await saveSeason(season);
    setDone(season);
    onDone?.(season);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('In Saison übernehmen')}
      width="max-w-lg"
      footer={
        done ? (
          <>
            <span className="flex-1 text-sm text-live">{t('Übernommen in „{name}“ ({n} Spieltage).', { name: done.name, n: done.days.length })}</span>
            <Link to={`/saison/${done.id}`} className="no-underline">
              <Button variant="primary">{t('Saison öffnen')}</Button>
            </Link>
            <Button variant="ghost" onClick={onClose}>
              {t('Schließen')}
            </Button>
          </>
        ) : (
          <>
            <div className="flex-1" />
            <Button variant="ghost" onClick={onClose}>
              {t('Abbrechen')}
            </Button>
            <Button variant="primary" onClick={run} disabled={!day || (selected === 'new' && !newName.trim())}>
              {already ? t('Spieltag aktualisieren') : t('Übernehmen')}
            </Button>
          </>
        )
      }
    >
      {!day ? (
        <p className="text-sm text-warn m-0">{t('Dieses Turnier hat noch keine ausgeloste Vorrunde — nichts zu übernehmen.')}</p>
      ) : (
        <div className="grid gap-3 text-sm">
          <p className="m-0 text-muted">
            {t('Übernommen werden die {what} von', { what: dayProp ? t('Tabellen aus der Datei') : t('aktuellen Tabellen') })} <b className="text-ink">{day.name}</b>:{' '}
            {t('{c} Mannschaften, {p} Paare, {s} Spieler. Ein späterer erneuter Import ersetzt den Spieltag.', { c: day.clubs.length, p: day.teams.length, s: day.players.length })}
          </p>
          <Select value={selected} onChange={(e) => setSelected(e.target.value)}>
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({t('{n} Spieltage', { n: s.days.length })})
              </option>
            ))}
            <option value="new">{t('+ Neue Saison …')}</option>
          </Select>
          {selected === 'new' && <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('Saison 2026/27')} />}
          {already && <div className="text-warn">{t('Dieser Spieltag ist schon in der Saison — wird mit den aktuellen Tabellen ersetzt.')}</div>}
        </div>
      )}
    </Dialog>
  );
}
